import _ from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
/**
 * List of entities
 * T represents type of items the list will hold
 * V represents type of dto
 */
export class EntityList {
    /**
     * List of items
     * @param rawData Data to be modelled as list items
     * @param Type Class name to instantiate items
     * @param generateId Generates ids automatically for items
     * Use this only for local entities
     * @param idKey Used when primary key is not named as 'id'
     * in the response object
     */
    constructor(rawData = [], Type, { generateId = false, idKey } = {}) {
        this.items = [];
        this.Type = Type;
        this.generateId = generateId;
        _.each(rawData, (item) => {
            if (idKey) {
                item["id"] = item[idKey];
            }
            const hasId = !!item.id;
            const newItem = new Type(item);
            if (!hasId && this.generateId) {
                _.set(newItem, "id", _.uniqueId("list_item_"));
            }
            if (!newItem.id) {
                throw new Error("All the list items must have an id");
            }
            this.addItem(newItem);
        });
        if (EntityList.hasDuplicateIds(this.items)) {
            throw new Error("List contains duplicate items");
        }
        makeObservable(this, {
            items: observable,
            size: computed,
            addItem: action,
            addItems: action,
            updateItem: action,
            removeItem: action,
            clear: action,
            replace: action,
            merge: action,
            reverse: action,
            splice: action,
            sort: action,
        });
    }
    get size() {
        return this.items.length;
    }
    addItem(item) {
        if (!item.id && this.generateId) {
            _.set(item, "id", _.uniqueId("list_item_"));
        }
        if (this.getItemById(item.id ?? "")) {
            this.updateItem(item);
        }
        else {
            this.items.push(item);
        }
        return this;
    }
    addItems(items) {
        _.each(items, (item) => this.addItem(item));
        return this;
    }
    updateItem(updatedItem) {
        if (updatedItem && this.hasItems()) {
            const hasItem = this.getItemById(updatedItem.id);
            if (hasItem) {
                const index = _.findIndex(this.items, (item) => item.id === updatedItem.id);
                this.items[index] = updatedItem;
            }
        }
        return this;
    }
    removeItem(id) {
        const deleteIndex = _.findIndex(this.items, (item) => item.id === id);
        if (deleteIndex >= 0) {
            this.items.splice(deleteIndex, 1);
        }
        return this;
    }
    clear() {
        while (this.hasItems()) {
            this.items.pop();
        }
        return this;
    }
    replace(list) {
        this.clear().addItems(list.items);
        return this;
    }
    merge(list) {
        this.addItems(list.items);
        return this;
    }
    hasItems() {
        return this.size > 0;
    }
    isEmpty() {
        return !this.hasItems();
    }
    getIds() {
        return _.map(this.items, (item) => item.id);
    }
    getItemById(id) {
        return _.find(this.items, (item) => item.id === id);
    }
    getItemByIndex(index) {
        return index < this.size ? this.items[index] : undefined;
    }
    first() {
        return _.first(this.items);
    }
    last() {
        return _.last(this.items);
    }
    find(iterator) {
        return _.find(this.items, iterator);
    }
    findIndex(iterator) {
        return _.findIndex(this.items, iterator);
    }
    getFilteredList(iterator, list) {
        list.addItems(_.filter(this.items, iterator));
        return list;
    }
    getIndex(id) {
        return _.findIndex(this.items, (item) => item.id === id);
    }
    reverse() {
        this.items.reverse();
        return this;
    }
    map(iterator) {
        return _.map(this.items, iterator);
    }
    each(iterator) {
        _.each(this.items, iterator);
    }
    some(iterator) {
        _.some(this.items, iterator);
    }
    filter(iterator) {
        return _.filter(this.items, iterator);
    }
    forEach(iterator) {
        return _.forEach(this.items, iterator);
    }
    orderBy(iterator, isDescending) {
        const orderedItems = _.orderBy(this.items, iterator, [
            isDescending ? "desc" : "asc",
        ]);
        this.clear().addItems(orderedItems);
    }
    splice(start, deleteCount = 1) {
        this.items.splice(start, deleteCount);
        return this;
    }
    slice(start, end) {
        return this.items.slice(start, end);
    }
    sort(compareFn) {
        this.items.sort(compareFn);
        return this;
    }
    uniqueBy(fieldName) {
        return _.uniqBy(this.items, fieldName);
    }
    getDtos() {
        const dtos = this.map((item) => item.getDto?.());
        return dtos.filter((dto) => !!dto);
    }
}
EntityList.hasDuplicateIds = (items) => {
    const group = _.groupBy(items, (item) => item.id);
    return _.some(group, (_items) => _items.length > 1);
};
